import { useTranslation } from "react-i18next";
import { type VatMode } from "../../context/CostDisplayContext.tsx";
import { useCurrentSession } from "../../context/CurrentSessionContext.tsx";
import { type RecipeDetailFragment } from "../../gql/generated/graphqlSchema.tsx";
import { formatNumber } from "../../utils/nutritionFormat.ts";
import { formatEur } from "../../utils/priceFormat.ts";

type ReferenceCost = RecipeDetailFragment["referenceCost"];

export const rowCost = (referenceCost: ReferenceCost, recipeIngredientId: string, vatMode: VatMode): number | null => {
  const row = referenceCost.rows.find((r) => r.recipeIngredientId === recipeIngredientId);
  if (row == null) return null;
  return (vatMode === "with" ? row.gross : row.net) ?? null;
};

export const totalCost = (referenceCost: ReferenceCost, vatMode: VatMode): number | null =>
  (vatMode === "with" ? referenceCost.totalGross : referenceCost.totalNet) ?? null;

/**
 * Formats an amount in the team currency; null becomes an em dash. While the
 * team (and thus the currency) hasn't loaded, falls back to a plain number so
 * real costs never render as blank cells.
 */
export const useCostFormatter = (): ((value: number | null) => string) => {
  const { i18n } = useTranslation();
  const { team } = useCurrentSession();
  const currency = team?.currency;
  return (value) => {
    if (value == null) return "—";
    if (currency == null) return formatNumber(value, i18n.language, 2);
    return formatEur(value, i18n.language, currency);
  };
};
